/* ============================================================
   La Nuit — predictive-search.js
   Drives the in-overlay live search experience:
     - Renders product suggestions into the dedicated
       [data-search-results] container (NOT injected dynamically).
     - Honours the active scope chip by filtering the suggestions on the
       product's real `type` (the suggest endpoint has no field syntax:
       `type:"X"` in `q` is read as three more words, which is why a
       scoped query used to return products from other scopes).
     - Ranks real word matches (title, type, tags, variant titles) above
       Shopify's fuzzy body matches, and drops the fuzzy ones whenever a
       real match exists ("lace" no longer lists four framed prints).
     - Keyboard navigation: ↑↓ to walk results, Enter to open, Esc
       to dismiss (Esc handled by header.js).
     - Recent searches stored in localStorage and surfaced under the
       trending column when the input is empty.
     - `Cmd/Ctrl + K` and `/` keyboard shortcuts open the search.
   ============================================================ */

(function () {
  const RECENT_KEY = 'lanuit-recent-searches';
  const RECENT_MAX = 6;

  const overlay = document.querySelector('[data-search-overlay]');
  if (!overlay) return;
  const input = overlay.querySelector('[data-search-input]');
  const form = overlay.querySelector('[data-search-form]');
  const resultsEl = overlay.querySelector('[data-search-results]');
  const emptyEl = overlay.querySelector('[data-search-empty]');
  const asideEl = overlay.querySelector('[data-search-aside]');
  const recentWrap = overlay.querySelector('[data-search-recent]');
  const recentList = overlay.querySelector('[data-search-recent-list]');
  const recentClear = overlay.querySelector('[data-search-recent-clear]');
  if (!input || !form || !resultsEl) return;

  const debounce = window.LaNuit?.debounce || ((fn, ms = 200) => {
    let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  });

  // -------- Recent searches (localStorage) --------
  function readRecent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; }
    catch { return []; }
  }
  function writeRecent(list) {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX))); } catch {}
  }
  function pushRecent(term) {
    if (!term || term.length < 2) return;
    const cur = readRecent().filter((t) => t.toLowerCase() !== term.toLowerCase());
    cur.unshift(term);
    writeRecent(cur);
    renderRecent();
  }
  function renderRecent() {
    if (!recentList || !recentWrap) return;
    const items = readRecent();
    if (items.length === 0) {
      recentWrap.setAttribute('hidden', '');
      return;
    }
    recentWrap.removeAttribute('hidden');
    recentList.innerHTML = items.map((t) => `
      <li>
        <button type="button" class="site-header__search-chip" data-search-term="${escapeHTML(t)}">
          <span class="site-header__search-chip-icon" aria-hidden="true">↺</span>
          <span>${escapeHTML(t)}</span>
        </button>
      </li>
    `).join('');
    recentList.querySelectorAll('[data-search-term]').forEach((chip) => {
      chip.addEventListener('click', () => {
        input.value = chip.getAttribute('data-search-term') || '';
        input.focus({ preventScroll: true });
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });
  }
  recentClear?.addEventListener('click', () => {
    writeRecent([]);
    renderRecent();
  });
  renderRecent();

  // -------- Predictive fetch --------
  let activeIndex = -1;
  let lastResults = [];

  function activeScope() {
    return (window.LaNuit && window.LaNuit.searchScope) || '';
  }

  const INTERNAL_TAG = 'internal-only';
  const FETCH_LIMIT = 10;

  const search = debounce(async (term) => {
    if (!term || term.length < 2) {
      showIdle();
      return;
    }
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=${FETCH_LIMIT}&resources[options][unavailable_products]=last`;
      const data = await window.LaNuit.fetchJSON(url);
      const products = data?.resources?.results?.products || [];
      // Guard against an older response landing after a newer keystroke.
      if (input.value.trim() !== term) return;
      lastResults = refine(products, term, activeScope());
      render(lastResults, term);
    } catch (err) {
      showIdle();
    }
  }, 200);

  /* Scope + relevance, done here because the suggest endpoint offers
     neither. `type` is the product type Shopify returns with each hit;
     the scope chip carries the same string. Internal-only products
     (raw materials tagged `internal-only`) never surface.

     Shopify pads the list with fuzzy guesses ("lace" also returned four
     prints, "wand" five massagers that only mention "and"). The rule:
     when every word typed starts a word in some product's title, type,
     tags, vendor or variant titles, the term is a real product word and
     only products carrying it in those fields or in their body are
     shown (inside a scope that can be nothing, the honest answer for
     "lace" in Framed art). When no product carries it in those fields
     ("print", "wand", a typo) Shopify's fuzzy list stands, scoped. The
     body alone cannot decide because the suggest JSON only carries an
     excerpt of it. */
  function refine(products, term, scope) {
    const words = term.toLowerCase().split(/\s+/).filter((w) => w.length >= 2);
    const visible = products.filter((p) => !(p.tags || []).includes(INTERNAL_TAG));
    const inScope = visible.filter((p) => !scope || (p.type || '') === scope);
    if (words.length === 0) return inScope.slice(0, 6);
    const tokens = (str) => String(str || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    const hitCount = (list) => words.filter((w) => list.some((h) => h.startsWith(w))).length;
    const score = (p) => {
      const strong = tokens([p.title, p.type, p.vendor, ...(p.tags || []), ...(p.variants || []).map((v) => v.title)].join(' '));
      const strongHits = hitCount(strong);
      const anyHits = hitCount(strong.concat(tokens(p.body)));
      return { p, strong: strongHits === words.length, real: anyHits === words.length, score: hitCount(tokens(p.title)) * 2 + anyHits };
    };
    const termIsReal = visible.some((p) => score(p).strong);
    const scored = inScope.map(score);
    const pool = termIsReal ? scored.filter((s) => s.real) : scored;
    return pool.sort((a, b) => b.score - a.score).map((s) => s.p).slice(0, 6);
  }

  function showIdle() {
    // No query — show aside (trending/recent) + empty hint on the main pane.
    asideEl?.removeAttribute('hidden');
    emptyEl?.removeAttribute('hidden');
    resultsEl.setAttribute('hidden', '');
    resultsEl.innerHTML = '';
    activeIndex = -1;
    overlay.classList.remove('is-typing');
  }

  function showResults() {
    emptyEl?.setAttribute('hidden', '');
    resultsEl.removeAttribute('hidden');
    overlay.classList.add('is-typing');
  }

  function render(products, term) {
    showResults();
    if (products.length === 0) {
      resultsEl.innerHTML = `
        <div class="site-header__search-no-results">
          <p class="site-header__search-no-results-headline">No matches for "${escapeHTML(term)}"</p>
          <p class="tiny muted">Try a different keyword, or browse all <a href="/collections/all">products</a>.</p>
        </div>
      `;
      activeIndex = -1;
      return;
    }
    const scopeLabel = (window.LaNuit && window.LaNuit.searchScopeLabel) || activeScope();
    const scopeChip = activeScope() ? `<span class="site-header__search-result-scope">in ${escapeHTML(scopeLabel)}</span>` : '';
    const cards = products.map((p, i) => `
      <a class="site-header__search-result" href="${p.url}" role="option" data-result-index="${i}" aria-selected="false">
        <span class="site-header__search-result-media">
          ${p.image
            ? `<img src="${p.image}&width=160" loading="lazy" alt="" width="56" height="70">`
            : '<span class="site-header__search-result-placeholder"></span>'}
        </span>
        <span class="site-header__search-result-body">
          <span class="site-header__search-result-title">${escapeHTML(p.title)}</span>
          <span class="site-header__search-result-price tiny muted">${p.price}</span>
        </span>
        <span class="site-header__search-result-arrow" aria-hidden="true">→</span>
      </a>
    `).join('');
    resultsEl.innerHTML = `
      <p class="site-header__search-result-head">Showing ${products.length} ${products.length === 1 ? 'match' : 'matches'} ${scopeChip}</p>
      <div class="site-header__search-result-list">${cards}</div>
      <a class="site-header__search-result-all" href="${searchPageUrl(term, activeScope())}">
        View all results for "${escapeHTML(term)}" →
      </a>
    `;
    activeIndex = -1;
  }

  function setActive(idx) {
    const items = resultsEl.querySelectorAll('[data-result-index]');
    if (!items.length) return;
    if (idx < 0) idx = items.length - 1;
    if (idx >= items.length) idx = 0;
    activeIndex = idx;
    items.forEach((el, i) => {
      const on = i === idx;
      el.setAttribute('aria-selected', String(on));
      el.classList.toggle('is-active', on);
      if (on) el.scrollIntoView({ block: 'nearest' });
    });
  }

  // -------- Wiring --------
  input.addEventListener('input', (e) => {
    const v = e.target.value.trim();
    if (v.length === 0) {
      showIdle();
    } else {
      search(v);
    }
  });

  input.addEventListener('keydown', (e) => {
    if (overlay.hasAttribute('hidden')) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
    else if (e.key === 'Enter') {
      const items = resultsEl.querySelectorAll('[data-result-index]');
      if (activeIndex >= 0 && items[activeIndex]) {
        e.preventDefault();
        items[activeIndex].click();
      }
    }
  });

  form.addEventListener('submit', () => {
    const term = (input.value || '').trim();
    pushRecent(term);
  });

  // -------- Global keyboard shortcuts: Cmd/Ctrl+K and `/` --------
  document.addEventListener('keydown', (e) => {
    const tag = (e.target?.tagName || '').toLowerCase();
    const inField = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
    // Cmd/Ctrl + K — works from anywhere.
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      document.querySelector('[data-search-toggle]')?.click();
      return;
    }
    // `/` shortcut — only when NOT in a form field.
    if (e.key === '/' && !inField && !overlay.matches(':not([hidden])')) {
      e.preventDefault();
      document.querySelector('[data-search-toggle]')?.click();
    }
  });

  // -------- Helpers --------
  /* The full search page understands Shopify's field syntax, so the
     scope travels as `product_type:"X"`; `-tag:internal-only` keeps the
     raw-material products out of the result count. header.js builds the
     same URL on submit; main-search.liquid strips both again for display. */
  function searchPageUrl(term, scope) {
    if (window.LaNuit && typeof window.LaNuit.searchPageUrl === 'function') return window.LaNuit.searchPageUrl(term, scope);
    return `/search?q=${encodeURIComponent(term)}&type=product`;
  }
  function escapeHTML(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
})();
